import { ESCALA, deveRotular, escurecer, formatarValor, formatarVariacao, gradienteVertical } from "../tema";
import { estiloRotulo, opcoesBase } from "./base";

/**
 * Família 13 — variação positiva x negativa.
 *
 * Uma coluna por item, verde para cima e vermelha para baixo a partir de
 * uma linha de zero: variação semanal dos preços, desempenho das praças. A
 * cor sai do sinal do próprio valor, então trocar os números não exige
 * recolorir nada.
 *
 * Como na coluna empilhada, o eixo Y fica "visível" sem linha nem rótulo —
 * é ele que desenha a linha do zero (plotLines some com visible:false).
 */
const modelo = {
  id: "variacao-colunas",
  label: "Variação positiva / negativa",
  descricao: "Altas em verde e quedas em vermelho a partir do zero. Ex.: variação semanal dos preços.",

  build(spec) {
    const base = opcoesBase(spec);
    const serie = spec.dados?.series?.[0] || {};
    const valores = (serie.valores || []).map((v) => (v === null ? null : Number(v)));
    const opcoesValor = spec.valores || {};
    const exibirEm = opcoesValor.exibirEm ?? "todos";
    const comoPercentual = spec.extras?.percentual !== false;
    const corAlta = spec.extras?.corAlta || "#22B14C";
    const corQueda = spec.extras?.corQueda || "#E03C3C";
    const rotulo = estiloRotulo(spec);
    const maiorAbsoluto = Math.max(1, ...valores.map((v) => Math.abs(v || 0)));

    return {
      ...base,
      chart: { ...base.chart, type: "column" },
      xAxis: {
        ...base.xAxis,
        categories: spec.dados?.categorias || [],
        lineWidth: 0,
        labels: { ...base.xAxis.labels, autoRotation: false },
      },
      yAxis: {
        ...base.yAxis,
        visible: true,
        labels: { enabled: false },
        lineWidth: 0,
        gridLineWidth: 0,
        // Simétrico: uma queda pequena não pode parecer maior que uma alta
        // grande só porque o eixo se ajustou a cada lado.
        min: spec.eixo?.min ?? -maiorAbsoluto * 1.3,
        max: spec.eixo?.max ?? maiorAbsoluto * 1.3,
        plotLines: [
          {
            value: 0,
            color: "rgba(255,255,255,0.65)",
            width: 1.5 * ESCALA,
            zIndex: 4,
          },
        ],
      },
      plotOptions: {
        ...base.plotOptions,
        column: {
          borderWidth: 0,
          groupPadding: 0.08,
          pointPadding: 0.06,
          dataLabels: {
            ...rotulo,
            crop: false,
            overflow: "allow",
            formatter() {
              return comoPercentual
                ? formatarVariacao(this.y, opcoesValor.decimais ?? 1)
                : `${this.y > 0 ? "+" : ""}${formatarValor(this.y, opcoesValor)}`;
            },
          },
        },
      },
      series: [
        {
          name: serie.nome || "Variação",
          data: valores.map((valor, i) => {
            const cor = (valor ?? 0) < 0 ? corQueda : corAlta;
            return {
              y: valor,
              // Queda desce a partir do zero: o degradê inverte para o tom
              // claro continuar encostado na linha do zero.
              color:
                (valor ?? 0) < 0
                  ? gradienteVertical(escurecer(cor, 0.45), cor)
                  : gradienteVertical(cor, escurecer(cor, 0.45)),
              dataLabels: { enabled: deveRotular(i, valores.length, exibirEm) },
            };
          }),
        },
      ],
    };
  },

  exemplo() {
    return {
      modelo: "variacao-colunas",
      titulo: [
        { texto: "Variação Semanal", cor: "#F0F000" },
        { texto: " dos Preços ", cor: "#FFFFFF" },
        { texto: "(%)", cor: "#FFFFFF", tamanho: 8 },
      ],
      subtitulo: [{ texto: "Semana de 7 a 11 de setembro", cor: "#4EA6FF" }],
      fonte: "Fonte: IPAR RURAL BUSINESS",
      fundo: "#000000",
      fundoPlot: "nenhum",
      legenda: { visivel: false },
      dados: {
        categorias: ["Soja", "Milho", "Boi", "Café", "Trigo", "Algodão"],
        series: [{ nome: "Variação", valores: [2.4, -1.8, 0.9, -3.2, 1.1, -0.6] }],
      },
      valores: { decimais: 1, prefixo: "", sufixo: "", exibirEm: "todos" },
      rotulo: { corTexto: "#FFFFFF", corFundo: "#1A1A1A", corBorda: "#FFFFFF" },
      extras: { percentual: true, corAlta: "#22B14C", corQueda: "#E03C3C" },
    };
  },
};

export default modelo;
